/**
 * Service for resending confirmation emails
 */

import { getEmailRedirectUrl } from '@/utils/authRedirectUtils';

interface ResendResponse {
  success: boolean;
  message?: string;
  error?: string;
  rateLimited?: boolean;
  retryAfter?: number; // seconds
}

/**
 * Resend confirmation email for a user
 * @param email The user's email address
 * @returns Promise resolving to resend status
 */
export async function resendConfirmationEmail(email: string): Promise<ResendResponse> {
  try {
    console.log('Resending confirmation email for:', email);
    
    // Use the Supabase client to resend confirmation
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: getEmailRedirectUrl(),
      }
    });

    if (error) {
      console.error('Resend error:', error);
      
      // Handle rate limiting
      if (error.message?.includes('rate limit') || error.message?.includes('too many')) {
        return { 
          success: false, 
          error: 'Too many resend attempts. Please wait before trying again.',
          rateLimited: true,
          retryAfter: 300 // 5 minutes
        };
      }
      
      // Handle other errors
      return { 
        success: false, 
        error: error.message || 'Failed to resend confirmation email' 
      };
    }

    return { 
      success: true, 
      message: 'Confirmation email resent successfully' 
    };
  } catch (error) {
    console.error('Error resending confirmation email:', error);
    return { 
      success: false, 
      error: 'Failed to connect to email service' 
    };
  }
}

/**
 * Check if user can resend confirmation email (rate limiting check)
 * @param email The user's email address
 * @returns Promise resolving to whether resend is allowed
 */
export async function canResendConfirmationEmail(email: string): Promise<boolean> {
  try {
    // Check localStorage for last resend time
    const lastResendKey = `last_resend_${email}`;
    const lastResend = localStorage.getItem(lastResendKey);
    
    if (lastResend) {
      const lastResendTime = parseInt(lastResend);
      const now = Date.now();
      const timeDiff = now - lastResendTime;
      const minInterval = 2 * 60 * 1000; // 2 minutes
      
      return timeDiff >= minInterval;
    }
    
    return true;
  } catch (error) {
    console.error('Error checking resend eligibility:', error);
    return true; // Default to allowing resend if check fails
  }
}

/**
 * Record that a resend was attempted for rate limiting
 * @param email The user's email address
 */
export function recordResendAttempt(email: string): void {
  try {
    const lastResendKey = `last_resend_${email}`;
    localStorage.setItem(lastResendKey, Date.now().toString());
  } catch (error) {
    console.error('Error recording resend attempt:', error);
  }
}
